import { useMemo, useState } from 'react';

import type {
  GetPendingRxChangesDetailedParams,
  GetRxChangeDetailedResponseT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { usePendingRxChangesDetailedQuery } from '@/entities/rx';
import { toTableCellText } from '@/shared/lib/table';
import { Badge } from '@/shared/ui/shadcn/badge';
import { Button } from '@/shared/ui/shadcn/button';
import { Card, CardContent } from '@/shared/ui/shadcn/card';

import { buildMedicationLabel, buildRxChangeTypeLabel } from '../lib';
import { useWorklistPagination } from '../model';
import { RxChangeDetailSheet } from './RxChangeDetailSheet';
import { WorklistPagination } from './WorklistPagination';
import { WorklistEmpty, WorklistError, WorklistLoading } from './WorklistStates';

export interface PendingRxChangesListProps {
  readonly patientId?: number | null;
}

function getRxChangeRowKey(rxChange: GetRxChangeDetailedResponseT, index: number): string {
  return rxChange.RxChangeRequestId !== null && rxChange.RxChangeRequestId !== undefined
    ? String(rxChange.RxChangeRequestId)
    : `rxchange-${index}`;
}

export function PendingRxChangesList({ patientId }: PendingRxChangesListProps) {
  const { pageNumber, nextPage, previousPage } = useWorklistPagination(1);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const params = useMemo<GetPendingRxChangesDetailedParams>(
    () => ({ patientId: patientId ?? undefined, pageNumber }),
    [patientId, pageNumber]
  );

  const rxChangesQuery = usePendingRxChangesDetailedQuery(params);

  const items = useMemo<ReadonlyArray<GetRxChangeDetailedResponseT>>(
    () => rxChangesQuery.data?.Items ?? [],
    [rxChangesQuery.data]
  );
  const pageResult = rxChangesQuery.data?.PageResult ?? null;

  const selectedRxChange = useMemo<GetRxChangeDetailedResponseT | null>(() => {
    if (selectedKey === null) return null;
    const match = items.find(
      (rxChange, index) => getRxChangeRowKey(rxChange, index) === selectedKey
    );
    return match ?? null;
  }, [items, selectedKey]);

  if (rxChangesQuery.isPending) {
    return <WorklistLoading label="Loading pending RxChanges…" />;
  }

  if (rxChangesQuery.isError) {
    return (
      <WorklistError
        title="Could not load pending RxChanges"
        error={rxChangesQuery.error}
        onRetry={() => {
          void rxChangesQuery.refetch();
        }}
      />
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        <WorklistEmpty
          label="No pending RxChange requests."
          hint="Pharmacy-initiated change requests land here for clinician review."
        />
        <WorklistPagination
          pageNumber={pageNumber}
          pageResult={pageResult}
          onPrevious={previousPage}
          onNext={nextPage}
          isFetching={rxChangesQuery.isFetching}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-2" aria-label="Pending RxChange requests">
        {items.map((rxChange, index) => {
          const medication = buildMedicationLabel(rxChange.PrescribedPrescription);
          const requestedDate = toTableCellText(rxChange.RequestedDate);
          const changeTypeLabel = buildRxChangeTypeLabel(
            rxChange.RxChangeType,
            rxChange.RxChangeSubType
          );
          const sourceLabel = toTableCellText(rxChange.Source);
          const rxChangeId = toTableCellText(rxChange.RxChangeRequestId);
          const rowKey = getRxChangeRowKey(rxChange, index);

          return (
            <li key={rowKey}>
              <Card>
                <CardContent className="flex flex-col gap-2 px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{medication}</div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Badge variant="secondary" className="rounded-full">
                        RxChange #{rxChangeId}
                      </Badge>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedKey(rowKey);
                        }}
                        aria-label={`View RxChange ${rxChangeId} details`}
                      >
                        View details
                      </Button>
                    </div>
                  </div>
                  <div className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    <span>Type {changeTypeLabel}</span>
                    <span>Requested {requestedDate}</span>
                    <span>Source {sourceLabel}</span>
                  </div>
                </CardContent>
              </Card>
            </li>
          );
        })}
      </ul>

      <WorklistPagination
        pageNumber={pageNumber}
        pageResult={pageResult}
        onPrevious={previousPage}
        onNext={nextPage}
        isFetching={rxChangesQuery.isFetching}
      />

      <RxChangeDetailSheet
        rxChange={selectedRxChange}
        open={selectedRxChange !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setSelectedKey(null);
        }}
      />
    </div>
  );
}
